import React, { useState, useEffect, useRef } from "react";
import { getFirestore, collection, orderBy, getDocs, limit, query, setDoc, doc, updateDoc, arrayUnion, runTransaction } from "firebase/firestore";
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from "moment";
import Autocomplete from '@mui/material/Autocomplete';
import { getCuadrillasOnce } from "../../services/cuadrillasService";
import { getHerramientasOnce } from "../../services/herramientasOnceService";

const firestore = getFirestore();
const currentUser = JSON.parse(localStorage.getItem("currentUser"));

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AddRemitoHerramientas() {
    const [cuadrillas, setCuadrillas] = useState([]);
    const [herramientas, setHerramientas] = useState([]);
    const [stockHerramientas, setStockHerramientas] = useState([]); // stock levels for origin/destination
    const [spinner, setSpinner] = useState(false);
    const [saveOk, setSaveOk] = useState(false);
    const [openPreviewModal, setOpenPreviewModal] = useState(false);
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });
    const [selectedTools, setSelectedTools] = useState([]);

    const [state, setState] = useState({
        lastId: -1,
        materialsToSave: [], // stored as { id, name, value, observaciones } to match materials schema
        cuadrillaEntrega: '0', // defaults to Depósito cba
        cuadrillaReceptora: '',
        tipoRemito: 'ENTREGA',
        date: moment(new Date().getTime()),
        observaciones: '',
        origenManual: '',
        esOrigenManual: false,
    });

    useEffect(() => {
        getCuadrillasOnce(setCuadrillas);
        getHerramientasOnce(setHerramientas);
        fetchStockData();
    }, []);

    const fetchStockData = async () => {
        try {
            const snap = await getDocs(collection(firestore, 'herramientasxcuadrilla'));
            setStockHerramientas(snap.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        } catch (error) {
            console.error("Error al obtener stock de herramientas:", error);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    // When tipoRemito changes, automatically set the corresponding Depósito crew (ID 0)
    const handleTipoRemitoChange = (e) => {
        const tipo = e.target.value;
        if (tipo === 'ENTREGA') {
            setState(prev => ({
                ...prev,
                tipoRemito: tipo,
                cuadrillaEntrega: '0',
                cuadrillaReceptora: prev.cuadrillaReceptora === '0' ? '' : prev.cuadrillaReceptora,
                esOrigenManual: false
            }));
        } else if (tipo === 'DEVOLUCION') {
            setState(prev => ({
                ...prev,
                tipoRemito: tipo,
                cuadrillaReceptora: '0',
                cuadrillaEntrega: prev.cuadrillaEntrega === '0' ? '' : prev.cuadrillaEntrega,
                esOrigenManual: false
            }));
        } else if (tipo === 'INGRESO') {
            setState(prev => ({
                ...prev,
                tipoRemito: tipo,
                cuadrillaReceptora: '0',
                cuadrillaEntrega: '',
                esOrigenManual: true
            }));
        }
    };

    const changeQuantity = (e, id, name) => {
        const value = e.target.value;
        const formatData = { id, name, value };
        const existing = state.materialsToSave.find(item => item.id === id);
        
        let newMaterials = [...state.materialsToSave];
        if (existing) {
            if (value === '' || parseInt(value, 10) === 0) {
                newMaterials = newMaterials.filter(item => item.id !== id);
            } else {
                newMaterials = newMaterials.map(item => item.id === id ? { ...item, value } : item);
            }
        } else if (value !== '' && parseInt(value, 10) > 0) {
            newMaterials.push(formatData);
        }

        setState(prev => ({ ...prev, materialsToSave: newMaterials }));
    };

    const handleSave = async () => {
        const isIngreso = state.tipoRemito === 'INGRESO' || state.esOrigenManual;
        const origenInvalido = isIngreso ? !state.origenManual?.trim() : !state.cuadrillaEntrega;

        if (origenInvalido || !state.cuadrillaReceptora) {
            setSnackbarInfo({ text: 'Seleccione las cuadrillas u origen y destino.', type: 'warning', open: true });
            return;
        }
        if (state.materialsToSave.length === 0) {
            setSnackbarInfo({ text: 'Debe ingresar al menos una herramienta con cantidad mayor a 0.', type: 'warning', open: true });
            return;
        }

        // Validate stock in origin cuadrilla (solamente para transferencias normales)
        if (!isIngreso && state.cuadrillaEntrega) {
            let stockInsufficiente = false;
            state.materialsToSave.forEach(tool => {
                const docId = `${state.cuadrillaEntrega}c${tool.id}`;
                const stockDoc = stockHerramientas.find(s => s.id === docId);
                const currentStock = stockDoc ? parseInt(stockDoc.cantidad, 10) || 0 : 0;
                if (currentStock < parseInt(tool.value, 10)) {
                    stockInsufficiente = true;
                }
            });

            if (stockInsufficiente) {
                setSnackbarInfo({ text: 'Atención: Algunas herramientas no tienen suficiente stock en la cuadrilla de origen.', type: 'warning', open: true });
            }
        }

        setOpenPreviewModal(true);
    };

    const executeSaveAll = async () => {
        setOpenPreviewModal(false);
        setSpinner(true);

        try {
            const remitosRef = collection(firestore, "remitos_herramientas");
            const q = query(remitosRef, orderBy("id", "desc"), limit(1));
            const querySnapshot = await getDocs(q);

            let lastId = 0;
            if (!querySnapshot.empty) {
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data && data.id) {
                        lastId = data.id;
                    }
                });
            }
            const newId = lastId + 1;

            const isIngreso = state.tipoRemito === 'INGRESO' || state.esOrigenManual;
            const cuadrillaEntregaObj = cuadrillas.find(cuad => String(cuad?.id) === String(state.cuadrillaEntrega));
            const cuadrillaReceptoraObj = cuadrillas.find(cuad => String(cuad?.id) === String(state.cuadrillaReceptora));
            const nameCuadrillaEntrega = isIngreso ? (state.origenManual.trim() || 'PROVEEDOR') : (cuadrillaEntregaObj?.numero || '');
            const nameCuadrillaReceptora = cuadrillaReceptoraObj?.numero || '';
            const dateHour = moment(new Date().getTime()).format("DD/MM/YYYY HH:mm");

            const remitoData = {
                id: newId,
                materials: state.materialsToSave,
                tipoRemito: state.tipoRemito,
                fecha: moment(state.date).format("YYYYMMDD"),
                cuadrillaEntrega: nameCuadrillaEntrega,
                cuadrillaReceptora: nameCuadrillaReceptora,
                idCuadrillaEntrega: isIngreso ? 'PROVEEDOR' : state.cuadrillaEntrega,
                idCuadrillaReceptora: state.cuadrillaReceptora,
                observaciones: state.observaciones,
                confectionDate: dateHour
            };

            await setDoc(doc(firestore, "remitos_herramientas", newId.toString()), remitoData);

            const updatePromises = [];
            
            const updateToolStock = async (cuadrillaId, tool, factor) => {
                const docId = `${cuadrillaId}c${tool.id}`;
                const docRef = doc(firestore, 'herramientasxcuadrilla', docId);
                const existing = stockHerramientas.find(s => s.id === docId);
                
                const oldQty = existing ? parseInt(existing.cantidad, 10) || 0 : 0;
                const newQty = oldQty + (factor * parseInt(tool.value, 10));

                const registroStock = {
                    fecha: new Date().toISOString(),
                    cantidad: newQty,
                    cantidadAnterior: oldQty,
                    diferencia: factor * parseInt(tool.value, 10),
                    usuario: currentUser?.email || currentUser?.user || currentUser?.usuario || '-',
                    tipo: state.tipoRemito === 'ENTREGA' ? 'remito_entrega' : (state.tipoRemito === 'INGRESO' ? 'ingreso_proveedor' : 'remito_devolucion'),
                    idRemito: newId
                };

                if (existing) {
                    return updateDoc(docRef, {
                        cantidad: newQty,
                        historialStock: arrayUnion(registroStock)
                    });
                } else {
                    return setDoc(docRef, {
                        cantidad: newQty,
                        idCuadrilla: parseInt(cuadrillaId, 10),
                        idHerramienta: parseInt(tool.id, 10),
                        historialStock: [registroStock]
                    });
                }
            };

            state.materialsToSave.forEach(tool => {
                // Si no es ingreso de proveedor, restar del origen
                if (!isIngreso && state.cuadrillaEntrega) {
                    updatePromises.push(updateToolStock(state.cuadrillaEntrega, tool, -1));
                }
                // Sumar al destino
                updatePromises.push(updateToolStock(state.cuadrillaReceptora, tool, 1));
            });

            await Promise.all(updatePromises);

            setState(prev => ({ ...prev, lastId: newId }));
            setSaveOk(true);
            setSnackbarInfo({ text: 'Remito creado y stock actualizado correctamente.', type: 'success', open: true });
        } catch (error) {
            console.error('Error al guardar remito de herramientas:', error);
            setSnackbarInfo({ text: 'Ocurrió un error al guardar los datos.', type: 'error', open: true });
        } finally {
            setSpinner(false);
        }
    };

    if (saveOk) {
        return (
            <Container component="main" maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" sx={{ mb: 3 }}>
                        ✅ Remito de Herramientas creado correctamente!
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant="contained"
                            size="large"
                            href={`/imprimir-herramienta/${state.lastId}`}
                        >
                            Ver e Imprimir Remito
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => {
                                setSaveOk(false);
                                setSelectedTools([]);
                                setState({
                                    lastId: -1,
                                    materialsToSave: [],
                                    cuadrillaEntrega: '0',
                                    cuadrillaReceptora: '',
                                    tipoRemito: 'ENTREGA',
                                    date: moment(new Date().getTime()),
                                    observaciones: '',
                                    origenManual: '',
                                    esOrigenManual: false,
                                });
                                fetchStockData();
                            }}
                        >
                            Crear Otro Remito
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Nuevo Remito de Herramientas</Typography>
            
            {spinner ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                fullWidth
                                label="Tipo Remito"
                                value={state.tipoRemito}
                                onChange={handleTipoRemitoChange}
                            >
                                <MenuItem value="ENTREGA">Entrega (Salida Depósito)</MenuItem>
                                <MenuItem value="DEVOLUCION">Devolución (Entrada Depósito)</MenuItem>
                                <MenuItem value="INGRESO">Ingreso (Proveedor / Compra)</MenuItem>
                            </TextField>
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                            {state.tipoRemito === 'INGRESO' || state.esOrigenManual ? (
                                <TextField
                                    fullWidth
                                    required
                                    label="Proveedor / Origen"
                                    value={state.origenManual}
                                    onChange={(e) => setState(prev => ({ ...prev, origenManual: e.target.value }))}
                                    placeholder="Ej: Nucleo, Ferretería Central, etc."
                                />
                            ) : (
                                <TextField
                                    select
                                    fullWidth
                                    label="De la cuadrilla (Origen)"
                                    value={state.cuadrillaEntrega}
                                    onChange={(e) => setState(prev => ({ ...prev, cuadrillaEntrega: e.target.value }))}
                                >
                                    <MenuItem value="">Seleccione cuadrilla...</MenuItem>
                                    {cuadrillas.map(c => (
                                        <MenuItem key={c.id} value={String(c.id)}>
                                            {c.usuario ? `${c.usuario} - ${c.numero}` : c.numero}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                fullWidth
                                label="A la cuadrilla (Destino)"
                                value={state.cuadrillaReceptora}
                                onChange={(e) => setState(prev => ({ ...prev, cuadrillaReceptora: e.target.value }))}
                            >
                                <MenuItem value="">Seleccione cuadrilla...</MenuItem>
                                {cuadrillas.map(c => (
                                    <MenuItem key={c.id} value={String(c.id)}>
                                        {c.usuario ? `${c.usuario} - ${c.numero}` : c.numero}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Fecha"
                                    inputFormat="DD/MM/YYYY"
                                    value={state.date}
                                    onChange={(val) => setState(prev => ({ ...prev, date: val }))}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Observaciones"
                                value={state.observaciones}
                                onChange={(e) => setState(prev => ({ ...prev, observaciones: e.target.value }))}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Selección de Herramientas</Typography>
                    
                    <Autocomplete
                        multiple
                        options={herramientas.filter(h => h.visible !== false)}
                        getOptionLabel={(option) => option.name || option.nombre}
                        value={selectedTools}
                        onChange={(event, newValue) => {
                            setSelectedTools(newValue);
                            setState(prev => ({
                                ...prev,
                                materialsToSave: prev.materialsToSave.filter(item => newValue.some(t => t.id === item.id))
                            }));
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Buscar y agregar herramientas..."
                                placeholder="Seleccionar..."
                            />
                        )}
                        sx={{ mb: 4 }}
                    />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {selectedTools.map(tool => {
                            const originDocId = `${state.cuadrillaEntrega}c${tool.id}`;
                            const originStockDoc = stockHerramientas.find(s => s.id === originDocId);
                            const currentOriginStock = originStockDoc ? parseInt(originStockDoc.cantidad, 10) || 0 : 0;
                            
                            const valToSave = state.materialsToSave.find(item => item.id === tool.id)?.value || '';

                            return (
                                <Box key={tool.id} sx={{ flexBasis: '24%', minWidth: '220px', mb: 2 }}>
                                    <TextField
                                        label={tool.name || tool.nombre}
                                        type="number"
                                        placeholder={`Stock origen: ${currentOriginStock}`}
                                        value={valToSave}
                                        onChange={(e) => changeQuantity(e, tool.id, tool.name || tool.nombre)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSave}
                        >
                            Confirmar Remito
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Preview Modal */}
            <Dialog open={openPreviewModal} onClose={() => setOpenPreviewModal(false)} maxWidth="md" fullWidth>
                <DialogTitle>Confirmar Remito de Herramientas</DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Por favor confirme los detalles del remito:
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6}>
                                <strong>Tipo:</strong> {state.tipoRemito}
                            </Grid>
                            <Grid item xs={6}>
                                <strong>Fecha:</strong> {state.date.format("DD/MM/YYYY")}
                            </Grid>
                            <Grid item xs={6}>
                                <strong>Origen:</strong> {state.tipoRemito === 'INGRESO' || state.esOrigenManual ? (state.origenManual || 'PROVEEDOR') : (state.tipoRemito === 'ENTREGA' ? 'Depósito cba' : cuadrillas.find(c => String(c.id) === String(state.cuadrillaEntrega))?.usuario || state.cuadrillaEntrega)}
                            </Grid>
                            <Grid item xs={6}>
                                <strong>Destino:</strong> {state.tipoRemito === 'DEVOLUCION' ? 'Depósito cba' : cuadrillas.find(c => String(c.id) === String(state.cuadrillaReceptora))?.usuario || state.cuadrillaReceptora}
                            </Grid>
                            {state.observaciones && (
                                <Grid item xs={12}>
                                    <strong>Observaciones:</strong> {state.observaciones}
                                </Grid>
                            )}
                        </Grid>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Herramienta</strong></TableCell>
                                    <TableCell align="right"><strong>Cantidad a transferir</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {state.materialsToSave.map(tool => (
                                    <TableRow key={tool.id}>
                                        <TableCell>{tool.name || tool.nombre}</TableCell>
                                        <TableCell align="right">{tool.value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPreviewModal(false)}>Cancelar</Button>
                    <Button onClick={executeSaveAll} variant="contained" color="success">Confirmar y Guardar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarInfo.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AddRemitoHerramientas;
